import quickselect from 'quickselect'

let mainCanvas: HTMLCanvasElement | null = null

function renderAudioformToCanvas (array: number[]) {
  if (mainCanvas === null) return

  const { scrollWidth: width, scrollHeight: height } = mainCanvas
  if (mainCanvas.width !== width || mainCanvas.height !== height) {
    mainCanvas.width = width
    mainCanvas.height = height
  }

  const ctx = mainCanvas.getContext('2d')!

  const imageData = ctx.createImageData(width, height)
  imageData.data.fill(0xff)
  const length = array.length

  for (let x = 0; x < width; x++) {
    const startIndex = x * length / width | 0
    const endIndex = (x + 1) * length / width | 0
    const intervalSamples = array.slice(startIndex, endIndex)

    let maxSample = -1
    let minSample = 1
    for (const sample of intervalSamples) {
      if (maxSample < sample) maxSample = sample
      if (minSample > sample) minSample = sample
    }
    const intervalLength = intervalSamples.length
    const q1Index = intervalLength * 1 / 4 | 0
    quickselect(intervalSamples, q1Index)
    const q1Sample = intervalSamples[q1Index]

    const q3Index = intervalLength * 3 / 4 | 0
    quickselect(intervalSamples, q3Index)
    const q3Sample = intervalSamples[q3Index]

    // maxSample = 1 → y = 0, minSample = -1 → y = height - 1
    const maxSampleY = ((1 - (maxSample + 1) / 2.0) * (height - 1) + 0.5) | 0
    const minSampleY = ((1 - (minSample + 1) / 2.0) * (height - 1) + 0.5) | 0
    for (let iy = maxSampleY; iy <= minSampleY; iy++) {
      const index = (iy * width + x) * 4
      imageData.data[index + 0] = 160
      imageData.data[index + 1] = 160
      imageData.data[index + 2] = 160
    }

    const q3SampleY = ((1 - (q3Sample + 1) / 2.0) * (height - 1) + 0.5) | 0
    const q1SampleY = ((1 - (q1Sample + 1) / 2.0) * (height - 1) + 0.5) | 0
    for (let iy = q3SampleY; iy <= q1SampleY; iy++) {
      const index = (iy * width + x) * 4
      imageData.data[index + 0] = 96
      imageData.data[index + 1] = 96
      imageData.data[index + 2] = 96
    }
  }

  ctx.putImageData(imageData, 0, 0)
}

let startTime = 0
let sampleRate = 0
let audioAdjustment = 0
const samples = [] as number[]
const gcSampleSec = 10
const maxMoveTime = 0.01

function adjustAudioTiming (e: MouseEvent) {
  const targetEl = e.target as HTMLDivElement
  const rect = targetEl.getBoundingClientRect()
  const y = e.clientY - rect.top
  const moveTime = y / (rect.bottom - rect.top) * maxMoveTime
  if (targetEl.id === 'mic-audio-adjustment-left') audioAdjustment -= moveTime
  if (targetEl.id === 'mic-audio-adjustment-right') audioAdjustment += moveTime
}

const showTooltips = false

export function audioVisualizerInitCanvas (newSampleRate: number) {
  const micAudioZoneEl = document.getElementById('mic-audio-zone')! as HTMLDivElement
  micAudioZoneEl.innerHTML = `
  <canvas id="mic-audio-canvas"></canvas>
  <svg id='cursor-position-overlay' viewBox="0 0 0.13 1" preserveaspectratio="none" xmlns="http://www.w3.org/2000/svg">
    <line class="vertical-line hidden" y0=0 y1=1 />
  </svg>
  <span id='cursor-position-overlay-text'></span>
  <div id='mic-audio-adjustment-left'>◀<br><br><br>◀◀</div>
  <div id='mic-audio-adjustment-right'>▶<br><br><br>▶▶</div>
  `

  startTime = performance.now() / 1000
  sampleRate = newSampleRate
  document.getElementById('mic-audio-adjustment-left')?.addEventListener('click', adjustAudioTiming)
  document.getElementById('mic-audio-adjustment-right')?.addEventListener('click', adjustAudioTiming)

  mainCanvas = document.getElementById('mic-audio-canvas') as HTMLCanvasElement
  const overlaySVG = document.getElementById('cursor-position-overlay') as HTMLElement
  const cursorTextSpan = document.getElementById('cursor-position-overlay-text') as HTMLSpanElement

  overlaySVG.addEventListener('mousemove', (e) => {
    const rect = overlaySVG!.getBoundingClientRect()
    const x = (e.clientX - rect.left)
    const time = (x / (rect.right - rect.left)) * 0.13 - 0.1

    const verticalLine = overlaySVG!.getElementsByClassName('vertical-line')[0] as SVGLineElement
    verticalLine.setAttribute('x1', (time + 0.1).toString())
    verticalLine.setAttribute('x2', (time + 0.1).toString())

    cursorTextSpan.innerHTML = `${(time * 1000).toFixed(1)}ms`
  })

  // modify rt-bar viewbox
  // TODO: this might NOT be an appropriate place to put this code. Maybe refactor later?
  const rtBar = document.getElementById('rt-bar')!
  rtBar.setAttribute('viewBox', '0 0 1.3 1')
}

export function audioVisualizerPushSamples (newSamples: Float32Array) {
  samples.push(...newSamples)

  if (samples.length > (gcSampleSec * 2) * sampleRate) {
    startTime += gcSampleSec
    samples.splice(0, gcSampleSec * sampleRate)
  }
}

let pendingTimeout: number | null = null

export function audioVisualizerRenderCanvas (referenceTime: number) {
  if (pendingTimeout !== null) {
    clearTimeout(pendingTimeout)
    pendingTimeout = null
  }
  renderCanvas(referenceTime)
}

function renderCanvas (referenceTime: number) {
  if (mainCanvas === null) return

  pendingTimeout = null

  const referenceIndex = ((referenceTime - audioAdjustment - startTime) * sampleRate) | 0
  const dataBeginIndex = (referenceIndex - 0.1 * sampleRate) | 0
  const dataEndIndex = (referenceIndex + 0.03 * sampleRate) | 0
  const renderOutput = new Array<number>(dataEndIndex - dataBeginIndex)
  renderOutput.fill(0)
  for (let i = dataBeginIndex; i < dataEndIndex; i++) {
    renderOutput[i - dataBeginIndex] = (i < 0 || i >= samples.length) ? 0 : samples[i]
  }

  renderAudioformToCanvas(renderOutput)

  if (dataEndIndex >= samples.length) {
    pendingTimeout = setTimeout(() => audioVisualizerRenderCanvas(referenceTime), 100)
  }
}
